<script setup lang="ts">
import { CheckIcon, ChevronsUpDownIcon } from "@lucide/vue";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils.ts";
import { comboBoxContentVariants, type ComboBoxVariant } from "@/components/forms/comboBoxVariants";
import type { AcceptableValue } from "reka-ui";
import { computed, useId, type HTMLAttributes } from "vue";

export interface ComboBoxItem extends Record<string, unknown> {
  /**
   * A unique identifier for the item
   */
  value?: AcceptableValue;
  /**
   * Display label for the item
   */
  label?: string;
}

export interface ComboBoxProps {
  /**
   * Override where the component can find the label for an item.
   * Default: `label`
   */
  itemLabel?: string;
  /**
   * Override where the component can find the item's value.
   * Default: `value`
   */
  itemValue?: string;
  /**
   * The items available in the box
   */
  items: ComboBoxItem[];
  /**
   * Display label when the search yield no results (empty text)
   */
  emptyText?: string;
  /**
   * Placeholder text for the search input
   */
  searchPlaceholder?: string;
  /**
   * Placeholder text for the select input
   */
  selectPlaceholder?: string;
  /**
   * Optional property name marking an item as a non-selectable section header.
   * When set, an item with a truthy value at this key starts a new group whose
   * heading is that value, and the following items belong to it. Items before
   * the first header form an initial heading-less group.
   */
  itemHeader?: string;
  /**
   * Extra classes forwarded to the (portaled) popover content, e.g. to raise its
   * z-index above a high-z overlay it is rendered inside.
   */
  contentClass?: HTMLAttributes["class"];
  /**
   * Extra classes for the trigger button (default: full width).
   */
  triggerClass?: HTMLAttributes["class"];
  /**
   * Disables the trigger, so the dropdown cannot be opened.
   */
  disabled?: boolean;
  /**
   * Visual style of the dropdown, see `comboBoxContentVariants`.
   * Default: `default`
   */
  variant?: ComboBoxVariant;
}

const props = withDefaults(defineProps<ComboBoxProps>(), {
  itemValue: "value",
  itemLabel: "label",
  variant: "default",
});

const isOpen = defineModel<boolean>("isOpen", { required: false, default: false });
const selectedItem = defineModel<AcceptableValue>("selectedItem", { required: false, default: null });
const comboBoxId = useId();

/**
 * Split the flat `items` into headed groups when `itemHeader` is provided.
 * Without it (the default), all items end up in a single heading-less group, so
 * the rendered output is identical to a plain flat list.
 */
const groupedItems = computed<{ heading?: string; items: ComboBoxItem[] }[]>(() => {
  const groups: { heading?: string; items: ComboBoxItem[] }[] = [];
  let current: { heading?: string; items: ComboBoxItem[] } | null = null;

  for (const item of props.items) {
    const headerText = props.itemHeader ? item[props.itemHeader] : undefined;
    if (headerText) {
      current = { heading: String(headerText), items: [] };
      groups.push(current);
    } else {
      if (!current) {
        current = { items: [] };
        groups.push(current);
      }
      current.items.push(item);
    }
  }

  return groups;
});
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        role="combobox"
        :aria-expanded="isOpen"
        :disabled="disabled"
        :class="cn('w-full justify-between', triggerClass)"
      >
        {{
          selectedItem
            ? items.find((item) => item[itemValue] === selectedItem)?.[itemLabel]
            : selectPlaceholder || "Select item…"
        }}
        <ChevronsUpDownIcon class="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent :class="cn('w-full p-0', comboBoxContentVariants[variant], contentClass)" align="start">
      <Command>
        <CommandInput :placeholder="searchPlaceholder || 'Search…'" />
        <CommandList>
          <CommandEmpty>{{ emptyText || "No results" }}</CommandEmpty>
          <CommandGroup
            v-for="(group, gi) in groupedItems"
            :key="`combo-box-${comboBoxId}-group-${gi}`"
            :heading="group.heading"
          >
            <CommandItem
              v-for="(item,i) in group.items"
              :key="`combo-box-${comboBoxId}-item-${gi}-${i}-${item[itemValue]}`"
              :value="item[itemValue]!"
              class="cursor-pointer"
              @select="() => {
                selectedItem = (selectedItem === item[itemValue] ? '' : item[itemValue]) as AcceptableValue;
                isOpen = false;
              }"
            >
              <CheckIcon
                :class="
                  cn(
                    'mr-2 h-4 w-4',
                    selectedItem === item[itemValue] ? 'opacity-100' : 'opacity-0'
                  )
                "
              />
              {{ item[itemLabel] }}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
